import type { TableDefinition } from './definition';

/**
 * Classic Zero is the intentionally simple reference table.
 *
 * It is kept small on purpose: if this table does not play cleanly, the bug is
 * in the engine/renderer rather than in a complicated layout. It is also a good
 * starter template for the editor because every essential mechanism is easy to
 * see and understand.
 */
export const CLASSIC_ZERO: TableDefinition = {
  schemaVersion: 1,
  id: 'classic-zero',
  name: 'Classic Zero',
  authorInitials: 'BOP',
  board: {
    width: 52,
    height: 104,
    inclineDeg: 6.5,
    plungerMaxPower: 100,
    ballsPerGame: 3,
    ballSaverSeconds: 10,
    nudgeCount: 3,
  },
  theme: {
    accentColor: '#53e4ff',
  },
  rules: {
    comboWindowMs: 3000,
    comboStepBonus: 200,
    completionBonus: 8000,
    missions: [
      {
        id: 'classic-bells',
        name: 'Ring Both Bells',
        description: 'Strike each pop bumper once to light the return lanes.',
        trigger: 'bumper',
        partIds: ['bumper-left', 'bumper-right'],
        count: 2,
        bonus: 2000,
      },
      {
        id: 'classic-return-lanes',
        name: 'Light Both Returns',
        description: 'Roll through both lower return lanes before cashing the ramp.',
        trigger: 'rollover',
        partIds: ['roll-return-left', 'roll-return-right'],
        count: 2,
        bonus: 2500,
      },
      {
        id: 'classic-cash-ramp',
        name: 'Cash the Ramp',
        description: 'Send the ball up the main ramp to cash the simple three-step sequence.',
        trigger: 'ramp',
        partIds: ['ramp-main'],
        count: 1,
        bonus: 3500,
      },
    ],
  },
  parts: [
    // Continuous outside boundary. The bottom intentionally remains open so a
    // ball that passes the flippers reaches the drain sensor instead of hiding
    // behind decorative geometry.
    {
      id: 'wall-left',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.5,
      material: 'metal',
      points: [
        { x: 2, y: 18 },
        { x: 2, y: 100 },
      ],
    },
    {
      id: 'wall-right',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.5,
      material: 'metal',
      points: [
        { x: 50, y: 4 },
        { x: 50, y: 100 },
      ],
    },
    {
      id: 'wall-top-arch',
      kind: 'wall',
      scoreValue: 0,
      thickness: 1.5,
      material: 'metal',
      points: [
        { x: 50, y: 4 },
        { x: 43, y: 2 },
        { x: 31, y: 2 },
        { x: 18, y: 5 },
        { x: 9, y: 10 },
        { x: 2, y: 18 },
      ],
    },

    // Protected shooter lane. A deterministic launch path feeds the upper
    // right of the playfield before normal physics takes over.
    {
      id: 'plunger-lane',
      kind: 'plunger-lane',
      scoreValue: 0,
      material: 'metal',
      bounds: { x: 46, y: 8, width: 4, height: 92 },
      restPosition: { x: 48, y: 96 },
      divider: {
        points: [
          { x: 46, y: 18 },
          { x: 46, y: 96 },
        ],
        thickness: 1,
      },
      stop: { center: { x: 48, y: 100 }, width: 4, thickness: 1 },
      launchPath: [
        { x: 48, y: 96 },
        { x: 48, y: 28 },
        { x: 47, y: 12 },
        { x: 43, y: 7 },
        { x: 38, y: 10 },
      ],
    },

    // The required bells stay visually simple but now teach two different
    // contacts. The left bell is broad and soft enough to control; the right is
    // compact and kicks harder. Ring Both Bells therefore asks the reference
    // table player to manage two rebound profiles while preserving the equal
    // 500-point scoring contract used by the engine's baseline physics checks.
    {
      id: 'bumper-left',
      kind: 'bumper',
      scoreValue: 500,
      soundRef: 'lane-ping',
      center: { x: 20, y: 34 },
      radius: 3.8,
      impulse: 50,
      color: 'magenta',
    },
    {
      id: 'bumper-right',
      kind: 'bumper',
      scoreValue: 500,
      soundRef: 'pop-chime',
      center: { x: 32, y: 30 },
      radius: 3,
      impulse: 72,
      color: 'cyan',
    },

    // A single easy-to-read ramp shot. The ramp system is scripted in 2D
    // physics and elevated by the 3D renderer from this same path. Its rising
    // two-tone reward completes Classic Zero's simple three-step chime ladder.
    {
      id: 'ramp-main',
      kind: 'ramp',
      scoreValue: 1500,
      soundRef: 'save-rise',
      material: 'plastic',
      entry: { x: 35, y: 58 },
      path: [
        { x: 35, y: 58 },
        { x: 38, y: 47 },
        { x: 40, y: 35 },
        { x: 40, y: 23 },
        { x: 37, y: 14 },
        { x: 33, y: 11 },
      ],
      exit: { x: 33, y: 11 },
      exitVelocity: { x: -5, y: 18 },
    },

    // Conventional lower playfield. Keep the geometry simple, but let the two
    // recovery sides speak differently: a rubber snap on the left and a relay
    // coil clack on the right make lower-playfield direction readable by ear.
    {
      id: 'sling-left',
      kind: 'slingshot',
      scoreValue: 100,
      soundRef: 'rubber-snap',
      color: 'magenta',
      points: [
        { x: 11, y: 72 },
        { x: 19, y: 82 },
        { x: 11, y: 88 },
      ],
    },
    {
      id: 'sling-right',
      kind: 'slingshot',
      scoreValue: 100,
      soundRef: 'coil-clack',
      color: 'cyan',
      points: [
        { x: 41, y: 72 },
        { x: 33, y: 82 },
        { x: 41, y: 88 },
      ],
    },
    {
      id: 'guide-left-outlane',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.8,
      collider: true,
      material: 'metal',
      points: [
        { x: 5, y: 68 },
        { x: 5, y: 86 },
        { x: 12, y: 96 },
      ],
    },
    {
      id: 'guide-left-inlane',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.8,
      collider: true,
      material: 'metal',
      points: [
        { x: 13, y: 73 },
        { x: 14, y: 87 },
        { x: 18, y: 92 },
      ],
    },
    {
      id: 'guide-right-inlane',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.8,
      collider: true,
      material: 'metal',
      points: [
        { x: 39, y: 73 },
        { x: 38, y: 87 },
        { x: 34, y: 92 },
      ],
    },
    {
      id: 'guide-right-outlane',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.8,
      collider: true,
      material: 'metal',
      points: [
        { x: 44, y: 68 },
        { x: 44, y: 86 },
        { x: 40, y: 96 },
      ],
    },

    // The return lanes are now actual switches instead of decorative rails.
    // They sit inside the inlane corridors so controlled saves create useful
    // feedback and advance the classic electromechanical sequence.
    {
      id: 'roll-return-left',
      kind: 'rollover',
      scoreValue: 600,
      soundRef: 'lane-ping',
      center: { x: 16, y: 88.5 },
      radius: 2.1,
      laneId: 'classic-return-left',
      color: 'magenta',
    },
    {
      id: 'roll-return-right',
      kind: 'rollover',
      scoreValue: 600,
      soundRef: 'pop-chime',
      center: { x: 36, y: 88.5 },
      radius: 2.1,
      laneId: 'classic-return-right',
      color: 'cyan',
    },

    // Visible posts make the lower geometry read like a real machine and give
    // the ball predictable round contacts near the slings/inlanes.
    {
      id: 'post-left-outer',
      kind: 'post',
      scoreValue: 0,
      center: { x: 10.5, y: 71.5 },
      radius: 0.9,
      collider: true,
      material: 'rubber',
    },
    {
      id: 'post-left-inner',
      kind: 'post',
      scoreValue: 0,
      center: { x: 19.5, y: 82 },
      radius: 0.9,
      collider: true,
      material: 'rubber',
    },
    {
      id: 'post-right-inner',
      kind: 'post',
      scoreValue: 0,
      center: { x: 32.5, y: 82 },
      radius: 0.9,
      collider: true,
      material: 'rubber',
    },
    {
      id: 'post-right-outer',
      kind: 'post',
      scoreValue: 0,
      center: { x: 41.5, y: 71.5 },
      radius: 0.9,
      collider: true,
      material: 'rubber',
    },

    {
      id: 'flip-l',
      kind: 'flipper-left',
      scoreValue: 0,
      pivot: { x: 18, y: 94 },
      length: 9,
      restAngleDeg: 30,
      sweepDeg: 58,
      color: 'amber',
    },
    {
      id: 'flip-r',
      kind: 'flipper-right',
      scoreValue: 0,
      pivot: { x: 34, y: 94 },
      length: 9,
      restAngleDeg: 30,
      sweepDeg: 58,
      color: 'amber',
    },

    // A few non-colliding inserts add readable objectives without making the
    // reference table mechanically complicated.
    {
      id: 'insert-bumpers',
      kind: 'insert',
      scoreValue: 0,
      center: { x: 26, y: 45 },
      shape: 'diamond',
      width: 2.3,
      height: 2.3,
      color: 'magenta',
      emissive: 1.2,
    },
    {
      id: 'insert-ramp',
      kind: 'insert',
      scoreValue: 0,
      center: { x: 35, y: 63 },
      shape: 'arrow',
      width: 2.3,
      height: 4.5,
      rotationDeg: 180,
      color: 'cyan',
      emissive: 1.2,
    },

    { id: 'drain', kind: 'drain', scoreValue: 0, center: { x: 26, y: 103 }, width: 10 },
  ],
};
